import { type ActionCreator, on, type ReducerTypes } from '@ngrx/store';
import { type Draft, produce } from 'immer';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import { deepClone } from '@blaze-shared/shared-util';

import * as debuggerActions from './workflow-debugger.actions';
import {
  workflowDebuggerNodeSuccess,
  workflowDebuggerSetBreakpoint,
} from './workflow-debugger.actions';
import {
  initialWorkflowDebuggerSlice,
  type WorkflowDebuggerEngineState,
  type WorkflowDebuggerState,
} from './workflow-debugger.state';

export const workflowDebuggerReducerTypes: readonly ReducerTypes<
  WorkflowDebuggerEngineState,
  ActionCreator[]
>[] = [
  on(runtimeActions.applyWorkflow, (state, action) =>
    updateDebugger(state, (debug) => {
      const { workflow, workflowExecutionId, input, inputsData, debugMode } = action;

      if (debugMode) {
        debug.busy = true;
        debug.inProgress = true;
        debug.pendingNodeId = workflow.startNodeId;
        debug.executionId = workflowExecutionId;
        debug.contextData = {
          input,
          inputsData,
        };
        debug.nodeExecutionHistory = [];
      }
    }),
  ),

  on(debuggerActions.workflowDebuggerRunNextNode, (state) =>
    updateDebugger(state, (debug) => {
      debug.busy = true;
    }),
  ),

  on(workflowDebuggerSetBreakpoint, (state, { breakOnNode, stepOver, stepInto }) =>
    updateDebugger(state, (debug) => {
      debug.breakpoint = {
        breakOnNode,
        stepOver,
        stepInto,
      };
    }),
  ),

  on(debuggerActions.runWorkflowDebugger, (state, action) =>
    updateDebugger(state, (debug) => {
      debug.busy = true;
      debug.executionId = action.workflowExecutionId;
      debug.workflowId = action.workflowId;
      debug.workflow = action.workflow;
      debug.inProgress = true;
    }),
  ),

  on(debuggerActions.runWorkflowDebuggerError, (state) =>
    updateDebugger(state, (debug) => {
      debug.busy = false;
      debug.queuedStepPayload = undefined;
      debug.resumeToken = undefined;
      debug.pendingNodeId = undefined;
      debug.inProgress = false;
    }),
  ),

  // why not `updateDebugger`: stopping discards the whole slice rather than editing fields of it.
  on(debuggerActions.stopWorkflowDebugger, (state) =>
    state.workflowDebugger === undefined ? state : { ...state, ...initialWorkflowDebuggerSlice },
  ),

  on(debuggerActions.runWorkflowDebuggerSuccess, (state, { nodePayload, nodeId, prevNodeId }) =>
    updateDebugger(state, (debug) => {
      debug.busy = false;
      debug.queuedStepPayload = undefined;
      debug.pendingNodeId = undefined;
      debug.resumeToken = undefined;
      debug.inProgress = false;
      if (nodeId) {
        debug.nodeExecutionHistory = debug.nodeExecutionHistory || [];
        if (nodePayload) {
          debug.nodeExecutionHistory.push({
            nodeId,
            prevNodeId,
            nodePayload: deepClone(nodePayload),
          });
        }
      }
    }),
  ),

  on(workflowDebuggerNodeSuccess, (state, { nodePayload, nodeId }) =>
    updateDebugger(state, (debug) => {
      const { workflow, workflowExecutionId, workflowNode } = nodePayload;
      debug.executedNodeId = nodeId;
      debug.executionId = workflowExecutionId;
      debug.pendingNodeId = workflowNode?.id;
      debug.workflowId = workflow?.id;
      debug.busy = false;
      if (nodeId) {
        debug.nodeExecutionHistory = debug.nodeExecutionHistory || [];
        debug.nodeExecutionHistory.push({
          nodeId: nodeId,
          prevNodeId: nodePayload.prevNodeId,
          nodePayload: deepClone(nodePayload),
        });
      }

      debug.queuedStepPayload = deepClone(nodePayload);
    }),
  ),

  on(runtimeActions.connectionStepComplete, (state, { workflowExecutionId }) => {
    if (state.workflowDebugger?.executionId !== workflowExecutionId) {
      return state;
    }
    return updateDebugger(state, (debug) => {
      debug.pendingNodeId = undefined;
      debug.resumeToken = undefined;
      debug.queuedStepPayload = undefined;
      debug.busy = false;
    });
  }),

  on(debuggerActions.workflowDebuggerUpdateQueuedNodeContextPayload, (state, { ctx }) =>
    updateDebugger(state, (debug) => {
      if (!debug.queuedStepPayload) {
        return;
      }
      debug.queuedStepPayload.ctx = {
        ...debug.queuedStepPayload.ctx,
        ...ctx,
      };
    }),
  ),
];

/**
 * Applies `update` to the debugger slice. An action arriving while the slice is absent — a runtime
 * that registered no engine-state extension — leaves the state untouched.
 */
function updateDebugger(
  state: WorkflowDebuggerEngineState,
  update: (debuggerState: Draft<WorkflowDebuggerState>) => void,
): WorkflowDebuggerEngineState {
  return produce(state, (draft) => {
    if (!draft.workflowDebugger) {
      return;
    }
    update(draft.workflowDebugger);
  });
}
